import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Modal Content */}
          <div className="fixed inset-0 flex items-center justify-center p-4" onClick={onClose}>
            <motion.div
              className="w-full max-w-sm bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-center pt-8 pb-4">
                <Link 
                  className="flex items-center justify-center" 
                  to="/" 
                  onClick={onClose}
                >
                  <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M20 40C8.9543 40 0 31.0457 0 20S8.9543 0 20 0s20 8.9543 20 20-8.9543 20-20 20zm.7408-11.9472c.0146 2.2962 5.581 2.7797 8.4101 1.9174 3.8157-1.1629 5.4-3.3432 4.7995-6.5593-.5361-2.8716-7.2385-4.7076-6.7548-7.6109.4837-2.9032 5.4605-3.1658 5.4038-5.0344-.0334-1.099-.8676-1.3778-3.1357-.935-2.268.443-5.3373 3.1494-5.9375 4.9525-1.2983 3.9004 3.5193 5.892 3.5193 8.6278 0 2.7358-6.3214 2.0175-6.3047 4.6419zm-6.6431-8.6471c.2184-3.346 4.2932-2.887 4.4195-6.0521.1009-2.5258-5.7326-1.4675-8.6434.2641s-4.3303 3.9864-3.859 6.7462c.706 4.1341 8.447 10.0832 10.8449 8.1486 2.398-1.9345-2.9805-5.761-2.762-9.1068z"
                      fill="currentColor" fillRule="evenodd" />
                  </svg>
                </Link>
              </div>
              
              {/* Navigation Links */}
              <div className="px-6 pb-5">
                <div className="flex flex-col space-y-3">
                  <Link to="/" className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors" onClick={onClose}>
                    <div className={`transition ease-in-out duration-250 flex items-center justify-center w-8 h-8 rounded-lg ${
                      location.pathname === '/' 
                        ? 'bg-gradient-to-br from-blue-700 to-violet-800' 
                        : 'group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700'
                    }`}>
                      <svg className={`w-4 h-4 ${location.pathname === '/' ? 'text-white' : ''}`} fill="none" viewBox="0 0 16 16">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"
                          d="M8 1.333 2.667 4.667v6.666c0 .737.597 1.334 1.333 1.334h8c.737 0 1.334-.597 1.334-1.334V4.667L8 1.333Z" />
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"
                          d="M6 8h4" />
                      </svg>
                    </div>
                    <p className="text-sm font-mono-recursive">Home</p>
                  </Link>
                  
                  <Link to="/about" className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors" onClick={onClose}>
                    <div className={`transition ease-in-out duration-250 flex items-center justify-center w-8 h-8 rounded-lg ${
                      location.pathname === '/about' 
                        ? 'bg-gradient-to-br from-blue-700 to-violet-800' 
                        : 'group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700'
                    }`}>
                      <svg className={`w-4 h-4 ${location.pathname === '/about' ? 'text-white' : ''}`} fill="none" viewBox="0 0 20 20">
                        <path fill="currentColor" d="M9.583 10.833a.417.417 0 1 0 0-.834.417.417 0 0 0 0 .834Z" />
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                          d="m9.916 10.082 3.762-3.761m0 0c.313-.313.488-.736.489-1.178v-1.12c0-.442.175-.866.488-1.179l.928-.928a.833.833 0 0 1 1.167 0l1.333 1.333a.833.833 0 0 1 0 1.167l-.928.928a1.667 1.667 0 0 1-1.178.489h-1.12c-.442 0-.866.175-1.179.488ZM5 13.333l1.667 1.666m.166-6.75c.417-.916 1.333-1.583 2.333-1.583a4.126 4.126 0 0 1 4.167 4.167c0 1-.667 1.916-1.583 2.333l-.75.333a1.667 1.667 0 0 0-1 1.5 3.333 3.333 0 0 1-3.333 3.334c-2.75 0-5-2.25-5-5A3.333 3.333 0 0 1 5 9.999a1.667 1.667 0 0 0 1.5-1l.333-.75ZM10 10.416a.417.417 0 1 1-.833 0 .417.417 0 0 1 .833 0Z" />
                      </svg>
                    </div>
                    <p className="text-sm font-mono-recursive">About</p>
                  </Link>
                  
                  <a href="mailto:chris@christophersamuels.com" className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors" onClick={onClose}>
                    <div className="group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700 transition ease-in-out duration-250 flex items-center justify-center w-8 h-8 rounded-lg">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"
                          d="m14.667 4.666-5.98 3.8a1.293 1.293 0 0 1-1.373 0l-5.98-3.8m1.333-2h10.666c.737 0 1.334.597 1.334 1.333v8c0 .737-.597 1.334-1.333 1.334H2.667a1.333 1.333 0 0 1-1.333-1.334V4c0-.736.596-1.333 1.333-1.333Z" />
                      </svg>
                    </div>
                    <p className="text-sm font-mono-recursive">Contact</p>
                  </a>
                </div>
              </div>

              {/* Social Links */}
              <div className="px-6 pb-6">
                <div className="flex justify-center gap-8">
                  <a
                    href="https://www.linkedin.com/in/samuelschristopher/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center"
                  >
                    <div className="group-hover:bg-zinc-300 dark:group-hover:bg-zinc-700 transition ease-in-out duration-250 flex items-center justify-center w-10 h-10 rounded-xl">
                      <svg className="icon flex-shrink-0 w-5 h-5" fill="none" viewBox="0 0 20 20">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                          d="M13.333 6.666a5 5 0 0 1 5 5v5.833H15v-5.833a1.667 1.667 0 0 0-3.334 0v5.833H8.333v-5.833a5 5 0 0 1 5-5ZM5 7.5H1.667v10H5v-10ZM3.333 5a1.667 1.667 0 1 0 0-3.334 1.667 1.667 0 0 0 0 3.333Z" />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://codepen.io/chrissamuels"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center"
                  >
                    <div className="group-hover:bg-zinc-300 dark:group-hover:bg-zinc-700 transition ease-in-out duration-250 flex items-center justify-center w-10 h-10 rounded-xl">
                      <svg className="icon flex-shrink-0 w-5 h-5" fill="none" viewBox="0 0 20 20">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                          d="M2 12.69 10 18m-8-5.31V7.31m0 5.38 8-5.38M10 18l8-5.31M10 18v-5.31m8 0V7.31m0 5.38-8-5.38m8 0L10 2m8 5.31-8 5.308L2 7.31M10 2 2 7.31M10 2v5.31" />
                      </svg>
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Close Button - Fixed to bottom of viewport */}
          <div className="fixed bottom-8 left-0 right-0 flex justify-center z-10">
            <motion.button
              onClick={onClose}
              className="w-12 h-12 bg-white dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-full shadow-lg transition-colors flex items-center justify-center"
              aria-label="Close modal"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;
